import { Box, ButtonBase, Stack, Typography } from '@mui/material';

import { SITE_THEME_PRESETS } from '@/lib/site-styles/theme-presets';
import { fontCssStack } from '@/styles/theme/create-unit-site-theme';
import type { SiteThemeConfig } from '@/types/database';

const PALETTE_SLOTS = ['lightest', 'light', 'accent', 'dark', 'darkest'] as const;

interface ThemesSectionProps {
  themeConfig: SiteThemeConfig;
  onApplyPreset: (presetId: string) => void;
}

// Matches Scouting Ops Hub's Website > Style > Themes section: every preset
// as an equally-weighted card (name + description + palette strip), not
// the small horizontal-scroll swatch strip this used to be — see
// scoutingops-hub/src/components/app/website-settings/style-section.tsx.
export function ThemesSection({ themeConfig, onApplyPreset }: ThemesSectionProps) {
  return (
    <Box>
      <Typography variant="overline" color="text.secondary" sx={{ fontWeight: 700, display: 'block', mb: 0.5 }}>
        Themes
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
        Start from a preset, then fine-tune anything below.
      </Typography>
      <Stack direction="row" sx={{ flexWrap: 'wrap', gap: 1.5 }}>
        {SITE_THEME_PRESETS.map((preset) => {
          const isActive = JSON.stringify(preset.config) === JSON.stringify(themeConfig);
          return (
            <ButtonBase
              key={preset.id}
              onClick={() => onApplyPreset(preset.id)}
              sx={{
                width: 170,
                display: 'block',
                textAlign: 'left',
                borderRadius: 1.5,
                outline: '2px solid',
                outlineColor: isActive ? 'primary.main' : 'transparent',
                outlineOffset: 2,
              }}
            >
              <Stack sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1.5, overflow: 'hidden' }}>
                <Stack direction="row" sx={{ height: 40, flexShrink: 0 }}>
                  {PALETTE_SLOTS.map((slot) => (
                    <Box key={slot} sx={{ flex: 1, bgcolor: preset.config.palette[slot] }} />
                  ))}
                </Stack>
                <Stack sx={{ bgcolor: preset.config.palette.lightest, p: 1.25 }}>
                  <Typography
                    variant="subtitle2"
                    sx={{ color: preset.config.palette.darkest, fontFamily: fontCssStack(preset.config.fonts.headingFont) }}
                  >
                    {preset.name}
                  </Typography>
                  <Typography
                    variant="caption"
                    sx={{
                      color: preset.config.palette.dark,
                      mt: 0.25,
                      display: '-webkit-box',
                      WebkitLineClamp: 2,
                      WebkitBoxOrient: 'vertical',
                      overflow: 'hidden',
                      minHeight: '2.6em',
                    }}
                  >
                    {preset.description}
                  </Typography>
                </Stack>
              </Stack>
            </ButtonBase>
          );
        })}
      </Stack>
    </Box>
  );
}
